import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import ParentHeader from "../ParentHeader/ParentHeader";
import SiteHeader from "../../Site/SiteHeader";
import SiteFooter from "../../Site/SiteFooter";
import { signedIn as hasSession } from "../../Site/session";
import { usePageMeta } from "../../Site/useSiteMotion";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { LOGOUT } from "../../redux/actionTypes/authActionType";
import { DELETE_ACCOUNT_PATH, rememberDeleteAccountReturn } from "../../helper/accountDeletion";
import "../../Site/site.css";
import "./deleteAccount.css";

// /delete-account (MC2 card 072cd5b4): a parent deletes their own account.
// Public URL (Google Play's web deletion link): signed out it explains what
// deletion does and offers the login, which returns here. Signed in: current
// password, "Delete my account", an in-page confirm, then POST /delete-account.
// The words are the shared spec's, the same as the app's.

export const BODY =
  "Deleting your account removes your login, your name and email, and your phone's notifications. " +
  "Your dancers' entries and results stay with the events they took part in, with their names, " +
  "as part of the organisers' records. This can't be undone.";
export const DELETED_NOTICE = "Your account has been deleted.";

// The confirm is a real modal (review R1-F1, the Results Status confirm's
// pattern): Tab and Shift+Tab cycle inside it, the page behind it is inert,
// Escape is Cancel, and closing it puts the focus back on "Delete my account".
// It renders outside the page wrapper (into `host`) so the wrapper can be
// made inert without taking the dialog with it.
const ConfirmDialog = ({ busy, onCancel, onConfirm }) => {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  // keep the focus inside, also while both buttons are disabled (deleting)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.contains(document.activeElement)) return;
    (dialog.querySelector("button:not([disabled])") || dialog).focus();
  });
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (!busy) onCancel();
      return;
    }
    if (e.key !== "Tab") return;
    const items = [...dialogRef.current.querySelectorAll("button:not([disabled])")];
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
      e.preventDefault();
      first.focus();
    }
  };
  return (
    <div className="da-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div
        ref={dialogRef}
        className="da-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="da-confirm-h"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 id="da-confirm-h">Delete your account?</h2>
        <div className="da-dialog-acts">
          <button type="button" className="da-btn ghost" ref={cancelRef} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="da-btn danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteForm = ({ pageRef, host }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const submitRef = useRef(null);
  const wasConfirming = useRef(false);
  const [password, setPassword] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // the page behind the confirm is inert while it is open
  useEffect(() => {
    const page = pageRef.current;
    if (!confirming || !page) return undefined;
    page.setAttribute("inert", "");
    page.setAttribute("aria-hidden", "true");
    return () => {
      page.removeAttribute("inert");
      page.removeAttribute("aria-hidden");
    };
  }, [confirming, pageRef]);
  // closing the confirm (Cancel, Escape, a refusal) returns the focus to
  // "Delete my account"; runs after the cleanup above lifts the inert
  useEffect(() => {
    if (wasConfirming.current && !confirming) submitRef.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);

  const ask = (e) => {
    e.preventDefault();
    if (!password) return;
    setError("");
    setConfirming(true);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await serviceConsumer("POST", `${process.env.REACT_APP_BASE_URL}${DELETE_ACCOUNT_PATH}`, { password });
      // Signed out locally, as Logout does (LogoutModal): the server has
      // already ended every session of the account, so no logout call.
      dispatch({ type: LOGOUT });
      localStorage.clear();
      navigate("/", { replace: true, state: { accountDeleted: true } });
    } catch (err) {
      // every refusal (401 WRONG_PASSWORD, 403 NOT_SELF_SERVICE,
      // 409 PAYMENT_PROCESSING, 429 TOO_MANY_ATTEMPTS, 503
      // PAYMENT_CHECK_FAILED) shows the server's own words
      setBusy(false);
      setConfirming(false);
      setError(typeof err?.messages === "string" && err.messages !== "ServerError"
        ? err.messages
        : "Your account wasn't deleted. Please try again.");
    }
  };

  return (
    <form className="da-form" onSubmit={ask} noValidate>
      <label className="da-label" htmlFor="da-password">Current password</label>
      <input
        id="da-password"
        className="da-in"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => { setPassword(e.target.value); setError(""); }}
        aria-invalid={!!error}
        aria-describedby={error ? "da-error" : undefined}
      />
      {error && <p className="da-error" id="da-error" role="alert">{error}</p>}
      <button type="submit" ref={submitRef} className="da-btn danger da-submit" disabled={!password || busy}>
        Delete my account
      </button>
      {confirming && host && createPortal(
        <ConfirmDialog busy={busy} onCancel={() => setConfirming(false)} onConfirm={remove} />,
        host,
      )}
    </form>
  );
};

const DeleteAccount = () => {
  const signedIn = hasSession();
  const pageRef = useRef(null);
  const [host, setHost] = useState(null);
  usePageMeta(
    "Delete your Feis.link account",
    "How to delete your Feis.link parent account, and what happens to your dancers' entries and results.",
  );

  return (
    <div className={`site da${signedIn ? "" : " da-out"}`}>
      <div className="da-page" ref={pageRef}>
        {signedIn ? <ParentHeader /> : <SiteHeader />}
        <main>
          <section className="hero da-hero">
            <div className="wrap">
              <span className="eyebrow">Account</span>
              <h1 className="da-title">Delete account</h1>
            </div>
          </section>
          <div className="wrap">
            <div className="da-card">
              <p className="da-body">{BODY}</p>
              {signedIn ? (
                <DeleteForm pageRef={pageRef} host={host} />
              ) : (
                <div className="da-acts">
                  <Link className="sbtn" to="/login" onClick={() => rememberDeleteAccountReturn()}>
                    Log in to delete your account
                  </Link>
                </div>
              )}
            </div>
          </div>
        </main>
        {!signedIn && <SiteFooter />}
      </div>
      <div ref={setHost} />
    </div>
  );
};

export default DeleteAccount;
